import type { ReactNode } from 'react';

import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  PersonOutline as OutpatientIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';

import type { StaffRole } from '@/store/authStore';
import { useAuthStore } from '@/store/authStore';
import { FEATURES } from '@/config/features';
import { useUiStore } from '@/store/uiStore';

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 64;

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  roles?: StaffRole[];
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: '대시보드', icon: <DashboardIcon /> },
  { to: '/residents', label: '입소자 관리', icon: <PeopleIcon /> },
  // 외래는 협의 완료 전까지 숨김 — FEATURES.outpatient 참고
  ...(FEATURES.outpatient
    ? [{ to: '/outpatient', label: '외래 관리', icon: <OutpatientIcon /> }]
    : []),
  { to: '/settings', label: '시스템 설정', icon: <SettingsIcon />, roles: ['ADMIN'] },
];

export function Sidebar() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggle = useUiStore((s) => s.toggleSidebar);
  const mobileOpen = useUiStore((s) => s.mobileSidebarOpen);
  const closeMobile = useUiStore((s) => s.closeMobileSidebar);
  const staff = useAuthStore((s) => s.staff);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const visibleItems = NAV_ITEMS.filter(
    (item) => !item.roles || (staff && item.roles.includes(staff.role)),
  );

  const width = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  const content = (
    <>
      <Toolbar />
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <List sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
          {visibleItems.map((item) => (
            <SidebarItem
              key={item.to}
              item={item}
              collapsed={!isMobile && collapsed}
              onNavigate={() => {
                if (isMobile) closeMobile();
              }}
            />
          ))}
        </List>
        {!isMobile && (
          <>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
              <Tooltip title={collapsed ? '메뉴 펼치기' : '메뉴 접기'} placement="right" arrow>
                <IconButton onClick={toggle} size="small">
                  {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </>
        )}
      </Box>
    </>
  );

  if (isMobile) {
    // md 미만: overlay drawer (Header 햄버거로 토글)
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={closeMobile}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: EXPANDED_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        '& .MuiDrawer-paper': {
          width,
          overflowX: 'hidden',
          transition: (t) =>
            t.transitions.create('width', {
              easing: t.transitions.easing.sharp,
              duration: t.transitions.duration.shorter,
            }),
          boxSizing: 'border-box',
        },
      }}
    >
      {content}
    </Drawer>
  );
}

function SidebarItem({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate: () => void;
}) {
  const location = useLocation();
  const selected = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);

  const button = (
    <ListItemButton
      component={NavLink}
      to={item.to}
      selected={selected}
      onClick={onNavigate}
      sx={{
        minHeight: 48,
        justifyContent: collapsed ? 'center' : 'flex-start',
        px: 2.5,
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: collapsed ? 0 : 2,
          justifyContent: 'center',
          color: selected ? 'primary.main' : 'inherit',
        }}
      >
        {item.icon}
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={item.label}
          slotProps={{
            primary: {
              fontWeight: selected ? 600 : 400,
              color: selected ? 'primary.main' : 'inherit',
            },
          }}
        />
      )}
    </ListItemButton>
  );

  return collapsed ? (
    <Tooltip title={item.label} placement="right" arrow>
      <Box>{button}</Box>
    </Tooltip>
  ) : (
    button
  );
}
